import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Edit, Save, Add } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import styles from './ProgramManagementPage.module.scss';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  usePrograms,
  useUpdateProgram,
  useProgramTypes,
} from '../../api/hooks/program.hooks';
import { 
  useProgramVersionsByProgram,

  useUpdateProgramVersion,

} from '../../api/hooks/useProgramVersions';
import { 
  useProgramLevels,
  useDeleteProgramLevel,
} from '../../api/hooks/useProgramLevels';
import { ProgramDto, ProgramVersionDto, ProgramLevelDto } from '../../api/types/dtos/program';
import ProgramSelector from './components/ProgramSelector';
import VersionAccordion from './components/VersionAccordion';
import LevelCard from './components/LevelCard';
import CreateVersionModal from './modals/CreateVersionModal';
import CreateLevelModal from './modals/CreateLevelModal';
import { moveUp, moveDown } from './utils/sequenceHelpers';
import { PRIVILEGES } from '../../api/types/dtos';
import { useHasPrivilege } from '../../components/authorization';

const ProgramManagementPage: React.FC = () => {
  const [selectedProgram, setSelectedProgram] = useState<ProgramDto | null>(null);
  const [editingProgram, setEditingProgram] = useState(false);
  const [programName, setProgramName] = useState('');
  const [programDescription, setProgramDescription] = useState('');
  const [programTypeId, setProgramTypeId] = useState<string>('');
  const [createVersionModalOpen, setCreateVersionModalOpen] = useState(false);
  const [createLevelModalOpen, setCreateLevelModalOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState<ProgramLevelDto | null>(null);
  
  const { selectedOrganization, loading: organizationLoading } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPrivilege: canEditProgram } = useHasPrivilege(PRIVILEGES.UPDATE_PROGRAMS_VERSION);
  const { hasPrivilege: canCreateVersion } = useHasPrivilege(PRIVILEGES.CREATE_PROGRAMS_VERSION);

  // All hooks must be called before any conditional returns
  const { 
    data: programs = [], 
    isLoading: programsLoading,
    error: programsError 
  } = usePrograms(
    selectedOrganization?.id || ''
  );

  const { data: versions = [], isLoading: versionsLoading } = useProgramVersionsByProgram(
    selectedOrganization?.id || '',
    selectedProgram?.id || '',
    { isActive: true }
  );

  const { data: levels = [], isLoading: levelsLoading } = useProgramLevels(
    selectedOrganization?.id || '',
    selectedProgram?.id || '',
    { isActive: true }
  );

  const updateProgram = useUpdateProgram(selectedOrganization?.id || '');
  const { data: programTypes = [] } = useProgramTypes(selectedOrganization?.id || '');
  const updateVersion = useUpdateProgramVersion(
    selectedOrganization?.id || '',
    selectedProgram?.id || ''
  );

  const deleteLevel = useDeleteProgramLevel(
    selectedOrganization?.id || '',
    selectedProgram?.id || ''
  );

  // Organization validation - after all hooks
  if (organizationLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!selectedOrganization?.id) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 3 }}>
          No se ha seleccionado una organización. Por favor, seleccione una organización para continuar.
        </Alert>
      </Container>
    );
  }

  const handleSelectProgram = (program: ProgramDto | null) => {
    setSelectedProgram(program);
    if (program) {
      setProgramName(program.name);
      setProgramDescription(program.description || '');
      setProgramTypeId(program.programTypeId || '');
      setEditingProgram(false);
    }
  };

  const handleSaveProgram = async () => {
    if (!selectedProgram) return;

    try {
      await updateProgram.mutateAsync({
        id: selectedProgram.id,
        data: {
          name: programName.trim(),
          description: programDescription.trim() || undefined,
          organizationId: selectedProgram.organizationId,
          ...(programTypeId ? { programTypeId } : {}),
        },
      });
      enqueueSnackbar('Programa actualizado exitosamente', {
        variant: 'success',
      });
      setEditingProgram(false);
    } catch (error) {
      enqueueSnackbar('Error al actualizar el programa', { variant: 'error' });
      console.error('Error updating program:', error);
    }
  };

  const handleMoveVersionUp = async (version: ProgramVersionDto) => {
    const result = moveUp(version, versions);
    if (!result) return;

    try {
      await Promise.all([
        updateVersion.mutateAsync({
          id: result.item.id,
          data: {
            name: result.item.name,
            description: result.item.description,
            programId: result.item.programId,
            documentUrl: result.item.documentUrl,
            website: result.item.website,
            sequence: result.item.sequence,
          },
        }),
        ...(result.swapItem
          ? [
              updateVersion.mutateAsync({
                id: result.swapItem.id,
                data: {
                  name: result.swapItem.name,
                  description: result.swapItem.description,
                  programId: result.swapItem.programId,
                  documentUrl: result.swapItem.documentUrl,
                  website: result.swapItem.website,
                  sequence: result.swapItem.sequence,
                },
              }),
            ]
          : []),
      ]);
      enqueueSnackbar('Orden actualizado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al actualizar el orden', { variant: 'error' });
    }
  };

  const handleMoveVersionDown = async (version: ProgramVersionDto) => {
    const result = moveDown(version, versions);
    if (!result) return;

    try {
      await Promise.all([
        updateVersion.mutateAsync({
          id: result.item.id,
          data: {
            name: result.item.name,
            description: result.item.description,
            programId: result.item.programId,
            documentUrl: result.item.documentUrl,
            website: result.item.website,
            sequence: result.item.sequence,
          },
        }),
        ...(result.swapItem
          ? [
              updateVersion.mutateAsync({
                id: result.swapItem.id,
                data: {
                  name: result.swapItem.name,
                  description: result.swapItem.description,
                  programId: result.swapItem.programId,
                  documentUrl: result.swapItem.documentUrl,
                  website: result.swapItem.website,
                  sequence: result.swapItem.sequence,
                },
              }),
            ]
          : []),
      ]);
      enqueueSnackbar('Orden actualizado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al actualizar el orden', { variant: 'error' });
    }
  };

  const handleEditVersion = (_version: ProgramVersionDto) => {
    // Edit version is handled by VersionAccordion component
  };

  const handleDeleteLevel = async (levelId: string) => {
    try {
      await deleteLevel.mutateAsync(levelId);
      enqueueSnackbar('Nivel eliminado exitosamente', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al eliminar el nivel', { variant: 'error' });
      console.error('Error deleting level:', error);
    }
  };

  const handleEditLevel = (level: ProgramLevelDto) => {
    setEditingLevel(level);
    setCreateLevelModalOpen(true);
  };

  const handleCloseLevelModal = () => {
    setCreateLevelModalOpen(false);
    setEditingLevel(null);
  };

  const sortedVersions = [...versions].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

  return (
    <Container maxWidth="xl" className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h4" component="h1">
          Gestión de Programas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Administre programas, versiones e items de programas
        </Typography>
      </Box>

      <Box className={styles.content}>
        <ProgramSelector
          programs={programs}
          selectedProgram={selectedProgram}
          onSelect={handleSelectProgram}
          loading={programsLoading}
        />

        {selectedProgram && (
          <>
            {/* Program Info Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Información del Programa</Typography>
                {canEditProgram && !editingProgram && (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setEditingProgram(true)}
                  >
                    Editar
                  </Button>
                )}
                {canEditProgram && editingProgram && (
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSaveProgram}
                    disabled={updateProgram.isPending}
                  >
                    Guardar
                  </Button>
                )}
              </Box>

              {editingProgram ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Nombre"
                    value={programName}
                    onChange={(e) => setProgramName(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Descripción"
                    value={programDescription}
                    onChange={(e) => setProgramDescription(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Programa</InputLabel>
                    <Select
                      value={programTypeId}
                      onChange={(e) => setProgramTypeId(e.target.value)}
                      label="Tipo de Programa"
                    >
                      <MenuItem value="">
                        <em>Sin tipo</em>
                      </MenuItem>
                      {programTypes.map((pt) => (
                        <MenuItem key={pt.id} value={pt.id}>
                          {pt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {selectedProgram.name}
                    </Typography>
                    {selectedProgram.programTypeName && (
                      <Chip
                        size="small"
                        label={selectedProgram.programTypeName}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  {selectedProgram.description && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedProgram.description}
                    </Typography>
                  )}
                </>
              )}
            </Paper>

            {/* Levels Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Niveles del Programa
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Administre los niveles del programa. Los niveles son necesarios para crear items de versión.
                </Typography>
              </Box>

              {levelsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : levels.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No hay niveles creados. Agregue el primer nivel para comenzar.
                </Alert>
              ) : (
                levels.map((level) => (
                  <LevelCard
                    key={level.id}
                    level={level}
                    onEdit={handleEditLevel}
                    onDelete={handleDeleteLevel}
                  />
                ))
              )}

              {canCreateVersion && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  fullWidth
                  size="large"
                  onClick={() => {
                    setEditingLevel(null);
                    setCreateLevelModalOpen(true);
                  }}
                >
                  Agregar Nivel
                </Button>
              )}
            </Paper>

            {/* Versions Section */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Versiones
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Organice las versiones e items de su programa
                </Typography>
              </Box>

              {versionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : sortedVersions.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No hay versiones creadas. Agregue la primera versión para comenzar.
                </Alert>
              ) : (
                sortedVersions.map((version) => (
                  <VersionAccordion
                    key={version.id}
                    version={version}
                    allVersions={sortedVersions}
                    programId={selectedProgram.id}
                    onMoveUp={handleMoveVersionUp}
                    onMoveDown={handleMoveVersionDown}
                    onEditVersion={handleEditVersion}
                  />
                ))
              )}

              {canCreateVersion && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  fullWidth
                  size="large"
                  onClick={() => setCreateVersionModalOpen(true)}
                >
                  Agregar Versión
                </Button>
              )}
            </Paper>

            <CreateVersionModal
              open={createVersionModalOpen}
              onClose={() => setCreateVersionModalOpen(false)}
              programId={selectedProgram.id}
              existingVersions={versions}
            />

            <CreateLevelModal
              open={createLevelModalOpen}
              onClose={handleCloseLevelModal}
              programId={selectedProgram.id}
              editingLevel={editingLevel}
            />
          </>
        )}

        {/* Error State */}
        {programsError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Error al cargar los programas. Por favor, intente refrescar la página.
          </Alert>
        )}

        {!selectedProgram && !programsError && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Seleccione un programa o cree uno nuevo para comenzar
            </Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default ProgramManagementPage;

